import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import {
  Star as StarIcon,
  KeyboardArrowDown as ChevronDownIcon,
  Close as CloseIcon,
  Restaurant as RestaurantIcon,
  ShoppingBag as ShoppingBagIcon,
  Home as HomeIcon,
  Wifi as WifiIcon,
  DirectionsCar as ParkingIcon,
  LocalAtm as AtmIcon,
  SportsEsports as PlaygroundIcon,
  Hotel as HotelIcon
} from '@mui/icons-material';
import { amenities } from './vehicleData';

// Amenities configuration
const AMENITIES_CONFIG = amenities.map(amenity => ({
  ...amenity,
  icon: amenity.id === 'restroom' ? HomeIcon :
        amenity.id === 'food' ? RestaurantIcon :
        amenity.id === 'shopping' ? ShoppingBagIcon :
        amenity.id === 'wifi' ? WifiIcon :
        amenity.id === 'parking' ? ParkingIcon :
        amenity.id === 'atm' ? AtmIcon :
        amenity.id === 'playground' ? PlaygroundIcon :
        amenity.id === 'hotel' ? HotelIcon : RestaurantIcon,
  color: amenity.id === 'restroom' ? '#3b82f6' :
         amenity.id === 'food' ? '#f97316' :
         amenity.id === 'shopping' ? '#ec4899' :
         amenity.id === 'wifi' ? '#06b6d4' :
         amenity.id === 'parking' ? '#6b7280' :
         amenity.id === 'atm' ? '#22c55e' :
         amenity.id === 'playground' ? '#f59e0b' :
         amenity.id === 'hotel' ? '#6366f1' : '#f97316',
  bgColor: amenity.id === 'restroom' ? '#eff6ff' :
           amenity.id === 'food' ? '#fff7ed' :
           amenity.id === 'shopping' ? '#fdf2f8' :
           amenity.id === 'wifi' ? '#ecfeff' :
           amenity.id === 'parking' ? '#f9fafb' :
           amenity.id === 'atm' ? '#f0fdf4' :
           amenity.id === 'playground' ? '#fffbeb' :
           amenity.id === 'hotel' ? '#eef2ff' : '#fff7ed',
  borderColor: amenity.id === 'restroom' ? '#3b82f6' :
               amenity.id === 'food' ? '#f97316' :
               amenity.id === 'shopping' ? '#ec4899' :
               amenity.id === 'wifi' ? '#06b6d4' :
               amenity.id === 'parking' ? '#6b7280' :
               amenity.id === 'atm' ? '#22c55e' :
               amenity.id === 'playground' ? '#f59e0b' :
               amenity.id === 'hotel' ? '#6366f1' : '#f97316'
}));

const AmenitiesSection = ({
  showAmenities,
  toggleAmenities,
  selectedAmenities,
  setSelectedAmenities
}) => {
  const handleAmenityToggle = (amenityId) => {
    setSelectedAmenities(prev => {
      const newSelection = prev.includes(amenityId)
        ? prev.filter(id => id !== amenityId)
        : [...prev, amenityId];
      return newSelection;
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Section Header */}
      <motion.button
        type="button"
        onClick={toggleAmenities}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderRadius: 8,
          backgroundColor: 'white',
          border: '1px solid #bbf7d0',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#f0fdf4';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'white';
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <StarIcon sx={{ color: '#16a34a', fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight="medium" sx={{ color: '#1e293b' }}>
            Amenities
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {showAmenities && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleAmenities();
              }}
              sx={{
                padding: '4px',
                borderRadius: '50%',
                '&:hover': { backgroundColor: '#fef2f2' },
                color: '#ef4444'
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          <motion.div
            animate={{ rotate: showAmenities ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDownIcon sx={{ color: '#6b7280', fontSize: 20 }} />
          </motion.div>
        </Box>
      </motion.button>

      {/* Section Content */}
      <AnimatePresence>
        {showAmenities && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                padding: 4,
                backgroundColor: 'white',
                borderRadius: 2,
                border: '1px solid #dcfce7',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            >
              {/* Amenities Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: 2
                }}
              >
                {AMENITIES_CONFIG.map((amenity) => {
                  const isSelected = selectedAmenities.includes(amenity.id);
                  const IconComponent = amenity.icon;
                  
                  return (
                    <motion.button
                      key={amenity.id}
                      type="button"
                      onClick={() => handleAmenityToggle(amenity.id)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8,
                        padding: '12px 8px',
                        borderRadius: 12,
                        border: `2px solid ${isSelected ? amenity.borderColor : '#e5e7eb'}`,
                        backgroundColor: isSelected ? amenity.bgColor : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.target.style.backgroundColor = '#f9fafb';
                          e.target.style.borderColor = '#d1d5db';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.borderColor = '#e5e7eb';
                        }
                      }}
                    >
                      <IconComponent
                        sx={{
                          fontSize: 24,
                          color: isSelected ? amenity.color : '#9ca3af'
                        }}
                      />
                      <Typography
                        variant="caption"
                        fontWeight="medium"
                        sx={{
                          fontSize: '12px',
                          color: isSelected ? amenity.color : '#6b7280',
                          textAlign: 'center'
                        }}
                      >
                        {amenity.name}
                      </Typography>
                    </motion.button>
                  );
                })}
              </Box>

              {/* Selected Amenities Summary */}
              {selectedAmenities.length > 0 && (
                <Box
                  sx={{
                    padding: 3,
                    backgroundColor: '#f0fdf4',
                    borderRadius: 2,
                    border: '1px solid #bbf7d0'
                  }}
                >
                  <Typography variant="body2" fontWeight="medium" sx={{ color: '#374151', mb: 2 }}>
                    Selected Amenities ({selectedAmenities.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedAmenities.map((amenityId) => {
                      const amenity = AMENITIES_CONFIG.find(a => a.id === amenityId);
                      return (
                        <Chip
                          key={amenityId}
                          label={amenity?.name}
                          size="small"
                          sx={{
                            backgroundColor: amenity?.bgColor,
                            color: amenity?.color,
                            border: `1px solid ${amenity?.borderColor}`,
                            fontSize: '11px',
                            height: 24
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Amenities Tips */}
              <Box
                sx={{
                  padding: 3,
                  backgroundColor: '#eff6ff',
                  borderRadius: 2,
                  border: '1px solid #bfdbfe'
                }}
              >
                <Typography variant="body2" fontWeight="medium" sx={{ color: '#374151', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  ⭐ Amenities Tips
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    • Select amenities that matter most for your charging stops
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    • More amenities = fewer station options but better experience
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    • Consider your typical charging duration and needs
                  </Typography>
                </Box>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default AmenitiesSection;
